'use client';

import React, { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { isLoggedOutUserStr } from '@puzzlio/core';
import { useUserLoading } from '@/app/providers/UserProvider';
import {
  hasSynced,
  isLoaded,
  loadScope,
  moveUnsentPlays,
  onPlaysChanged,
  playsVersion,
  requestPersistence,
} from './store';
import { holdScope, isSyncFailing } from './sync';

/*
  Whose plays a page shows and saves. App-wide it's the cookie player, whose id arrives with /api/me; a page
  embedding a game for a player of its own (Discord, a partner) nests a PlayScopeProvider naming that one.
*/

interface PlayScope {
  /** Null until the player id is known. */
  scope: string | null;
  /** False for a player whose plays stay in this browser and never reach the server. */
  sync: boolean;
  /** Whether the scope has been read from storage, so reads answer for it. */
  loaded: boolean;
}

const PlayScopeContext = createContext<PlayScope>({ scope: null, sync: true, loaded: false });

export const usePlayScope = () => useContext(PlayScopeContext);

/** Holds a scope for as long as this is mounted: loaded, synced, and kept in step with the page's visibility. */
export function PlayScopeProvider({
  scope,
  sync,
  children,
}: {
  scope: string | null;
  sync: boolean;
  children: React.ReactNode;
}) {
  const [loadedScope, setLoadedScope] = useState<string | null>(() => (scope && isLoaded(scope) ? scope : null));

  useEffect(() => {
    if (!scope) return;
    const hold = holdScope(scope, sync);
    let current = true;
    void hold.loaded.then(() => {
      if (current) setLoadedScope(scope);
    });
    return () => {
      current = false;
      hold.release();
    };
  }, [scope, sync]);

  return (
    <PlayScopeContext.Provider value={{ scope, sync, loaded: !!scope && loadedScope === scope }}>
      {children}
    </PlayScopeContext.Provider>
  );
}

/**
 * The cookie player's scope, for the whole app.
 *
 * Signing in moves the browser's plays to the account on the server, and the browser takes the account's id.
 * Plays not yet sent move with it here; everything else comes back with the account's first pull. Plays made
 * signed out stay where they are, as the server leaves them.
 */
export function CookiePlayScope({ children }: { children: React.ReactNode }) {
  const [user] = useUserLoading();
  const scope = user?.user_str ?? null;
  const signedIn = !!user?.auth;

  const previous = useRef<{ scope: string | null; signedIn: boolean }>({ scope, signedIn });
  const [ready, setReady] = useState<string | null>(scope);
  useEffect(() => {
    const before = previous.current;
    previous.current = { scope, signedIn };
    const signingIn =
      !!scope &&
      !!before.scope &&
      before.scope !== scope &&
      signedIn &&
      !before.signedIn &&
      !isLoggedOutUserStr(before.scope);
    if (!signingIn) {
      setReady(scope);
      return;
    }
    let current = true;
    void Promise.all([loadScope(before.scope!), loadScope(scope)]).then(() => {
      moveUnsentPlays(before.scope!, scope);
      if (current) setReady(scope);
    });
    return () => {
      current = false;
    };
  }, [scope, signedIn]);

  // An account holder's history is worth keeping through disk pressure. Only then, because one browser asks
  // the player, and a passing visitor shouldn't be.
  useEffect(() => {
    if (signedIn) void requestPersistence();
  }, [signedIn]);

  return (
    <PlayScopeProvider scope={ready === scope ? scope : null} sync>
      {children}
    </PlayScopeProvider>
  );
}

/** Re-renders the caller whenever the store changes, sync or local save, and returns the change count. */
export function usePlaysVersion() {
  return useSyncExternalStore(onPlaysChanged, playsVersion, () => 0);
}

/**
 * Whether the scope's plays can be drawn from: loaded, and synced once (a new browser's history is still on
 * its way until then). `failed` is that first sync failing, the offline case.
 */
export function useScopeReady(): { scope: string | null; ready: boolean; failed: boolean } {
  const { scope, sync, loaded } = usePlayScope();
  usePlaysVersion();
  const ready = !!scope && loaded && (!sync || hasSynced(scope));
  return { scope: ready ? scope : null, ready, failed: !ready && !!scope && loaded && isSyncFailing(scope) };
}
